/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import { packListTableColumn } from "utils/tableHelper/packListTableHelper";
import ContentShell from "components/ContentShell";
import ImagePreviewModal from "components/ImagePreviewModal";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  archivePacks,
  copyPack,
  deletePack,
  getActivePromoPacks,
  getPlayerPacks,
  unarchivePacks,
  updatePackStatus,
} from "services/packServices";
import ArchiveActionBar from "components/archive/ArchiveActionBar";
import ArchiveViewButton from "components/archive/ArchiveViewButton";
import useContentArchive from "components/archive/useContentArchive";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import { opacity2hex, sortArray } from "utils/miscUtils";
import SearchInput from "components/SearchInput";
import configData from "config/config.json";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import useConsent from "hooks/useConsent";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import { PackValidationDialog } from "components/PackValidationDialog";
import PromoPackActivationDialog from "components/PromoPackActivationDialog";

const useStyles = makeStyles((theme) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  previewButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    minWidth: "40px",
    height: "40px",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    "&:disabled": {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.grey[600],
    },
  },
}));

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: {
    handleDelete?: (data: any) => void;
    handleDisable?: (id: any) => void;
    handleCopyPack?: (id: any) => void;
    handleArchive?: (ids: string[]) => void;
    archived?: boolean;
  };
}

interface ImagePreviewCellProps {
  data: any;
  onPreview: (imageUrl: string, title: string) => void;
}

const ImagePreviewCell = ({ data, onPreview }: ImagePreviewCellProps) => {
  const styles = useStyles();

  const handlePreview = (e: any) => {
    e.stopPropagation();
    onPreview(data?.imageUrl, data?.name || "Pack Image");
  };

  return (
    <IconButton
      className={styles.previewButton}
      onClick={handlePreview}
      disabled={!data?.imageUrl}
      title={data?.imageUrl ? "Preview Image" : "No image available"}
    >
      <VisibilityIcon fontSize="small" />
    </IconButton>
  );
};

const RowActions = ({ rowId, data, actionHandlers }: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    handleDelete = () => {},
    handleDisable = () => {},
    handleCopyPack = () => {},
    handleArchive = () => {},
    archived = false,
  } = actionHandlers;

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEditClick = (event: any, data: any) => {
    handleClose(event);
    navigate(configData.routes.packs.edit?.replace(":id", data?._id));
  };

  const handleDisableClick = (event: any, id: any) => {
    handleClose(event);
    handleDisable(id);
  };

  const handleCopyPackClick = (event: any, id: any) => {
    handleClose(event);
    handleCopyPack(id);
  };

  const handleDeleteClick = (event: any, rowData: any) => {
    handleClose(event);
    handleDelete(rowData);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.PACK.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {archived ? (
          <MenuItem
            onClick={(e) => {
              handleClose(e);
              handleArchive([data?._id]);
            }}
          >
            Restore
          </MenuItem>
        ) : (
          [
            <MenuItem key="edit" onClick={(e) => handleEditClick(e, data)}>
              Edit
            </MenuItem>,
            <MenuItem
              key="status"
              onClick={(e) => handleDisableClick(e, data?._id)}
            >
              {data.isDisabled ? "Enable" : "Disable"}
            </MenuItem>,
            <MenuItem
              key="copy"
              onClick={(e) => handleCopyPackClick(e, data?._id)}
            >
              Copy Pack
            </MenuItem>,
            <MenuItem
              key="archive"
              disabled={!data?.isDisabled}
              onClick={(e) => {
                handleClose(e);
                handleArchive([data?._id]);
              }}
            >
              Archive
            </MenuItem>,
            hasDeletePermission ? (
              <MenuItem
                key="delete"
                onClick={(e) => handleDeleteClick(e, data)}
              >
                Delete
              </MenuItem>
            ) : null,
          ]
        )}
      </Menu>
    </>
  );
};

const PackList = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const [imagePreview, setImagePreview] = useState({
    open: false,
    imageUrl: "",
    title: "",
  });
  const [validationDialogData, setValidationDialogData] = useState({
    open: false,
    errorMessages: [],
    validationResults: [],
  });

  const [promoActivation, setPromoActivation] = useState<{
    open: boolean;
    packId: string | null;
    packName: string;
    replacing: { _id: string; name: string }[];
  }>({ open: false, packId: null, packName: "", replacing: [] });
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.PACK.UPDATE_ROLES,
  );

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: filteredList.length });

  const archive = useContentArchive({
    label: { singular: "pack", plural: "packs" },
    // A pack is in-game while it is enabled, so only a disabled pack may be archived.
    isLive: (row: any) => !row?.isDisabled,
    archive: archivePacks,
    unarchive: unarchivePacks,
    onCompleted: () => fetchData(searchValue),
    onViewChange: () => onChangePage(defaultPageNo),
  });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async (search?: any) => {
    setLoading(true);
    try {
      let payload: any = {
        pageNo,
        pageSize,
        archived: archive.archived,
        includePromo: true,
      };
      const searchParam = search !== undefined ? search : searchValue;
      if (searchParam) payload["search"] = searchParam;
      const resp = await getPlayerPacks(payload);
      setFilteredList(
        resp?.items?.map((el) => ({
          ...el,
          isDisabledText: el.isDisabled ? "YES" : "NO",
          rowDisabled: el["isDisabled"] ? true : false,
          copiedFrom: el?.copiedFrom || "-",
          preview: (
            <ImagePreviewCell data={el} onPreview={handleImagePreview} />
          ),
        })),
      );
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?.name ?? data?._id, id: data?._id });
  };

  const handleImagePreview = (imageUrl: string, title: string) => {
    setImagePreview({
      open: true,
      imageUrl,
      title,
    });
  };

  const handleCloseImagePreview = () => {
    setImagePreview({
      open: false,
      imageUrl: "",
      title: "",
    });
  };

  const onDelete = async () => {
    try {
      const resp = await deletePack(constentDetails.id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const onDisable = async (packId) => {
    const pack = filteredList.find((row: any) => row?._id === packId);

    // Enabling a promo pack replaces the live one, so confirm it first. Disabling never needs it.
    if (pack?.type === "Promo" && pack?.isDisabled) {
      try {
        const replacing = await getActivePromoPacks(packId);

        if (replacing.length) {
          setPromoActivation({
            open: true,
            packId,
            packName: pack?.name,
            replacing,
          });
          return;
        }
      } catch (error: any) {
        showSnackbar(
          error?.message || "Could not check the active Promo pack",
          "error",
        );
        return;
      }
    }

    await applyStatusChange(packId);
  };

  const applyStatusChange = async (packId) => {
    try {
      const resp = await updatePackStatus(packId);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
    } catch (error) {
      // Handle pack validation errors when enabling
      const errorDetails = error?.response?.data?.details;

      if (errorDetails?.code === 4008) {
        // CANNOT_ENABLE_INVALID_PACK - Show validation dialog
        const metadata = errorDetails.metadata || {};

        setValidationDialogData({
          open: true,
          errorMessages: metadata.errors || [],
          validationResults: metadata.validationResults || [],
        });
      } else {
        // Covers the schedule-window codes (4012/4013), whose server message names the blocking date.
        showSnackbar(error?.response?.data?.message || error.message, "error");
      }
    }
  };

  const onSearch = (value: any) => {
    _onSearch(value);
    onChangePage(defaultPageNo);
  };

  // Archived records are a read-only view, so their rows do not link into the editor.
  const rowHref = (data: any) =>
    hasEditPermission && !archive.archived
      ? configData.routes.packs.edit?.replace(":id", data?._id)
      : null;

  const createPack = () => navigate(configData.routes.packs.create);
  const navigateToBulkUpload = () =>
    navigate(configData.routes.packs.bulkUploadPacks);
  const onCopyPack = async (id) => {
    try {
      setLoading(true);
      const resp = await copyPack(id);
      await fetchData();
      showSnackbar(resp?.message);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    const initialColumns = packListTableColumn().filter(
      (col) => col.id !== "actions" || hasEditPermission,
    );
    setColumns(initialColumns);

    fetchData(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchValue, archive.archived]);

  return (
    <ContentShell
      title={archive.archived ? "Archived Packs" : "Player Pack List"}
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a pack"
            onSearch={onSearch}
          />
          <ArchiveViewButton archive={archive} />
          {!archive.archived &&
            sessionHelper.hasRole(PermittedRoles.PLAYER.PACK.CREATE_ROLES) && (
              <CustomButton onClick={navigateToBulkUpload}>
                Bulk Upload
              </CustomButton>
            )}
          {!archive.archived &&
            sessionHelper.hasRole(PermittedRoles.PLAYER.PACK.CREATE_ROLES) && (
              <CustomButton onClick={createPack}>Create</CustomButton>
            )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <ArchiveActionBar
          archive={archive}
          rows={filteredList}
          nameKey="name"
        />
        <Table
          columns={columns}
          data={filteredList}
          currentPage={pageNo}
          actions={RowActions}
          rowHref={rowHref}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          highlightAltRow={true}
          onColumnClick={onColumnClick}
          selectable
          selectedIds={archive.selectedIds}
          onSelectionChange={archive.setSelectedIds}
          rowSelectDisabledReason={archive.rowSelectDisabledReason}
          actionHandlers={{
            handleDelete: _openConsentDialog,
            handleDisable: onDisable,
            handleCopyPack: onCopyPack,
            handleArchive: archive.requestAction,
            archived: archive.archived,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
        <ImagePreviewModal
          open={imagePreview.open}
          onClose={handleCloseImagePreview}
          imageUrl={imagePreview.imageUrl}
          title={imagePreview.title}
          alt={`Preview of ${imagePreview.title}`}
        />
        <PromoPackActivationDialog
          open={promoActivation.open}
          packName={promoActivation.packName}
          replacing={promoActivation.replacing}
          onClose={() =>
            setPromoActivation({
              open: false,
              packId: null,
              packName: "",
              replacing: [],
            })
          }
          onConfirm={async () => {
            const packId = promoActivation.packId;
            setPromoActivation({
              open: false,
              packId: null,
              packName: "",
              replacing: [],
            });
            await applyStatusChange(packId);
          }}
        />
        <PackValidationDialog
          open={validationDialogData.open}
          errorMessages={validationDialogData.errorMessages}
          validationResults={validationDialogData.validationResults}
          onClose={() =>
            setValidationDialogData({
              open: false,
              errorMessages: [],
              validationResults: [],
            })
          }
        />
      </Box>
    </ContentShell>
  );
};

export default PackList;
